import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import {
  useViewClient,
  CLIENT_PROFILE_NAV_ITEMS,
} from '../../hooks/useViewClient';
import { CLIENT_LABELS } from '../../constants/labels';
import { CLIENT_LABELS as TL } from '../../constants/translations/labels';
import ViewClientErrorScreen from './ViewClientErrorScreen';
import ViewClientBasicInfo from './ViewClientBasicInfo';
import ViewClientBillingDetails from './ViewClientBillingDetails';
import ViewClientComplianceDetails from './ViewClientComplianceDetails';
import ViewClientSubscription from './ViewClientSubscription';
import ViewClientPermissions from './ViewClientPermissions';
import ViewClientFacilities from './ViewClientFacilities';
import CertificateList from '@/epics/certificateManagement/pages/certificateList/CertificateList';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';

/** Props for the ViewClientPage component. */
interface ViewClientPageProps {
  clientId: string;
}

/**
 * Top-level page component for viewing a single client's full profile.
 *
 * Orchestrates tabbed navigation via `InlineLayout`, rendering one sub-panel
 * per tab: BasicInformation, BillingDetails, ComplianceDetails, Facilities,
 * Subscription, and Permissions. Also displays:
 * - Profile avatar (image or initials fallback) with status badge
 * - "Edytuj klienta" action button
 * - Payment-pending banner with link to the subscription plan when unpaid
 *
 * Delegates data fetching and derived state (initials, status label, badge
 * classes) to the `useViewClient` hook. Shows `ViewClientErrorScreen` on
 * fetch failure and a full-screen `Loader` while loading.
 */
const ViewClientPage: React.FC<ViewClientPageProps> = ({ clientId }) => {
  const navigate = useNavigate();

  const {
    client,
    showErrorScreen,
    initials,
    statusLabel,
    statusBadgeClasses,
    isLoading,
    accessError,
  } = useViewClient(clientId);

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Step 2 mid-session 403 guard: if listFacilities, listEquipment, or
  // listFacilityUsers returns 403 after the Facilities tab is already rendered,
  // the entire page is replaced by <ErrorPage403 />.
  const [listAccessError, setListAccessError] = React.useState(false);
  const handleListAccessDenied = React.useCallback(
    () => setListAccessError(true),
    []
  );

  const isPaymentPending =
    client?.subscriptionDetails?.paymentStatus === 'unpaid';

  const profileImageUrl = client?.profileUrl?.url?.trim()
    ? client.profileUrl.url
    : undefined;

  const handleViewSubscription = () => {
    const planId = client?.subscriptionDetails?.subscriptionPlanId;
    if (planId) {
      navigate(`/Plany/Podgląd-planu/${planId}?tab=PodstawoweInformacje`);
    }
  };

  const profileHeader = (
    <>
      <div className="flex items-center gap-4">
        <div
          className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] font-medium overflow-hidden"
          style={{
            width: '64px',
            height: '64px',
            fontSize: '22px',
            lineHeight: '32px',
          }}
        >
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{ fontSize: '18px', lineHeight: '28px' }}
            >
              {client?.name ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{ fontSize: '14px', lineHeight: '20px' }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{ width: '6px', height: '6px' }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      <Button
        label="Edytuj klienta"
        variant={ButtonVariant.Primary}
        size="Medium"
        onClick={() => navigate(`/Klienci/Edycja-klienta/${clientId}`)}
        className="py-[10px]! px-[16px]! font-medium! text-[14px]! hover:cursor-pointer mt-1"
        style={{ borderRadius: '8px' }}
      />
    </>
  );

  if (!privilegesLoading && !privileges.clientView) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (listAccessError) {
    return <ErrorPage403 />;
  }

  return (
    <React.Fragment key={clientId}>
      {showErrorScreen ? (
        <ViewClientErrorScreen />
      ) : (
        <InlineLayout
          title={TL.VIEW_CLIENT_TITLE.polish}
          navItems={[...CLIENT_PROFILE_NAV_ITEMS]}
          contentByNavId={{
            BasicInformation: <ViewClientBasicInfo client={client ?? null} />,
            BillingDetails: (
              <ViewClientBillingDetails client={client ?? null} />
            ),
            ComplianceDetails: (
              <ViewClientComplianceDetails client={client ?? null} />
            ),
            Facilities: (
              <ViewClientFacilities
                clientId={clientId}
                onAccessDenied={handleListAccessDenied}
              />
            ),
            Subscription: (
              <ViewClientSubscription
                subscription={client?.subscriptionDetails}
              />
            ),
            Permissions: <ViewClientPermissions />,
            Certificates: (
              <CertificateList
                clientMode
                onAccessDenied={handleListAccessDenied}
              />
            ),
          }}
          header={profileHeader}
          searchParamKey="tab"
          defaultActiveId="BasicInformation"
          paymentBanner={
            isPaymentPending ? (
              <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-[8px] bg-[#FFF6E5] mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src="/assets/alert-triangle (1).svg"
                    alt=""
                    className="w-5 h-5 shrink-0"
                  />
                  <p className="text-bodyMedium text-warning-40 min-w-0">
                    <span className="font-medium">
                      {CLIENT_LABELS.VIEW_CLIENT_PAYMENT_BANNER_TITLE}
                    </span>{' '}
                    {CLIENT_LABELS.VIEW_CLIENT_PAYMENT_BANNER_BODY}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleViewSubscription}
                  className="shrink-0 text-[14px] font-medium text-[#2A4455] underline hover:text-[#223746] cursor-pointer bg-transparent border-0 p-0"
                  data-testid="view-client-payment-banner-link"
                >
                  {CLIENT_LABELS.VIEW_CLIENT_PAYMENT_BANNER_LINK}
                </button>
              </div>
            ) : null
          }
        />
      )}
    </React.Fragment>
  );
};

export default ViewClientPage;
